import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { aepAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ExternalLink, ChevronDown, ChevronUp, CheckCircle2, Loader2,
  AlertCircle, Info, ShieldCheck,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'missing',    label: 'Missing',    dot: 'bg-red-500',    pill: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'in_process', label: 'In Process', dot: 'bg-amber-500',  pill: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'complete',   label: 'Complete',   dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'na',         label: 'N/A',        dot: 'bg-slate-400',   pill: 'bg-slate-100 text-slate-500 border-slate-200' },
];

/**
 * AEP Certifications tab — self-service progress tracker.
 *
 * Props:
 *   agentNpn (string) — required. When `readOnly` is true, we pull the record
 *     via /aep/agent/{npn}/certifications so GA/MGA can read a downline agent.
 *     Otherwise we use /aep/my-certifications for the logged-in agent.
 *   readOnly (bool) — disables the status dropdown + notes.
 */
export const AepCertificationsTab = ({ agentNpn, readOnly = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCarrierId, setSavingCarrierId] = useState(null);
  const [savedRecently, setSavedRecently] = useState({}); // carrier_id -> timestamp
  const [expandedInstructions, setExpandedInstructions] = useState({}); // carrier_id -> bool

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = readOnly
        ? await aepAPI.agentCertifications(agentNpn)
        : await aepAPI.myCertifications();
      setRows(res.data.carriers || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to load certifications');
    } finally {
      setLoading(false);
    }
  }, [agentNpn, readOnly]);

  useEffect(() => { load(); }, [load]);

  const persistRow = async (carrier_id, status, notes) => {
    setSavingCarrierId(carrier_id);
    try {
      await aepAPI.upsertMyCert(carrier_id, status, notes);
      setSavedRecently((s) => ({ ...s, [carrier_id]: Date.now() }));
      setTimeout(() => {
        setSavedRecently((s) => {
          const next = { ...s };
          delete next[carrier_id];
          return next;
        });
      }, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      // reload to snap back to server state
      load();
    } finally {
      setSavingCarrierId(null);
    }
  };

  const onStatusChange = (carrier_id, newStatus) => {
    const row = rows.find((r) => r.carrier_id === carrier_id);
    setRows((prev) => prev.map((r) => r.carrier_id === carrier_id ? { ...r, status: newStatus } : r));
    persistRow(carrier_id, newStatus, row?.notes || '');
  };

  const onNotesBlur = (carrier_id, newNotes) => {
    const row = rows.find((r) => r.carrier_id === carrier_id);
    if ((row?.notes || '') === (newNotes || '')) return;
    persistRow(carrier_id, row.status || 'missing', newNotes);
  };

  const summary = useMemo(() => {
    const total = rows.length;
    const complete = rows.filter((r) => r.status === 'complete').length;
    const inProcess = rows.filter((r) => r.status === 'in_process').length;
    const missing = rows.filter((r) => r.status === 'missing').length;
    const na = rows.filter((r) => r.status === 'na').length;
    // Exclude N/A from the denominator. If everything is N/A, show 100%.
    const activeTotal = total - na;
    const pct = activeTotal === 0 ? 100 : Math.round((complete / activeTotal) * 100);
    return { total, complete, inProcess, missing, na, activeTotal, pct };
  }, [rows]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-10 text-center text-slate-500">
          <Loader2 className="mx-auto animate-spin" size={20} />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="aep-certifications-tab">
      {/* Progress + Summary */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <ShieldCheck size={18} className="text-sky-500" />
                AEP 2027 Certification Progress
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Self-reported. Update after you finish each carrier&apos;s certification course.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="aep-summary-complete">
                {summary.complete} Complete
              </Badge>
              <Badge className="bg-amber-50 text-amber-700 border border-amber-200" data-testid="aep-summary-in-process">
                {summary.inProcess} In Process
              </Badge>
              <Badge className="bg-red-50 text-red-700 border border-red-200" data-testid="aep-summary-missing">
                {summary.missing} Missing
              </Badge>
              {summary.na > 0 && (
                <Badge className="bg-slate-100 text-slate-500 border border-slate-200" data-testid="aep-summary-na">
                  {summary.na} N/A
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500"
                style={{ width: `${summary.pct}%` }}
                data-testid="aep-progress-bar"
              />
            </div>
            <span className="text-sm font-heading font-semibold text-slate-900 tabular-nums w-12 text-right">
              {summary.pct}%
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Carrier list */}
      {rows.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500 text-sm">
            No active AEP carriers. Ask an admin to add one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const statusMeta = STATUS_OPTIONS.find((s) => s.value === row.status) || STATUS_OPTIONS[0];
            const isSaving = savingCarrierId === row.carrier_id;
            const justSaved = !!savedRecently[row.carrier_id];
            const instrOpen = !!expandedInstructions[row.carrier_id];
            const hasInstr = !!(row.instructions && row.instructions.trim());
            return (
              <Card key={row.carrier_id} data-testid={`aep-row-${row.carrier_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    {/* Logo tile — Phase 27.30.18 */}
                    <InternalCarrierLogo row={row} />
                    {/* LEFT: carrier name + portal link + status dot */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`w-2.5 h-2.5 rounded-full ${statusMeta.dot} shrink-0`} />
                        <span className="font-heading font-semibold text-slate-900">
                          {row.carrier_name}
                        </span>
                        {row.portal_url && (
                          <a
                            href={row.portal_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 hover:underline"
                            data-testid={`aep-portal-link-${row.carrier_id}`}
                          >
                            Portal <ExternalLink size={12} />
                          </a>
                        )}
                        {justSaved && (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`aep-saved-${row.carrier_id}`}>
                            <CheckCircle2 size={12} /> Saved
                          </span>
                        )}
                        {isSaving && (
                          <Loader2 size={12} className="animate-spin text-slate-400" />
                        )}
                      </div>

                      {/* Instructions collapsible */}
                      {hasInstr && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setExpandedInstructions((s) => ({ ...s, [row.carrier_id]: !s[row.carrier_id] }))}
                            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800"
                            data-testid={`aep-instr-toggle-${row.carrier_id}`}
                          >
                            <Info size={12} />
                            {instrOpen ? 'Hide' : 'Show'} instructions
                            {instrOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          </button>
                          {instrOpen && (
                            <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-700 whitespace-pre-wrap" data-testid={`aep-instr-body-${row.carrier_id}`}>
                              {row.instructions}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Notes */}
                      <div className="mt-3">
                        <Textarea
                          defaultValue={row.notes || ''}
                          onBlur={(e) => !readOnly && onNotesBlur(row.carrier_id, e.target.value)}
                          placeholder="Notes (optional)"
                          className="text-sm min-h-[52px] resize-y"
                          disabled={readOnly}
                          data-testid={`aep-notes-${row.carrier_id}`}
                        />
                      </div>
                    </div>

                    {/* RIGHT: status dropdown */}
                    <div className="md:w-48 shrink-0">
                      <Select
                        value={row.status}
                        onValueChange={(v) => !readOnly && onStatusChange(row.carrier_id, v)}
                        disabled={readOnly}
                      >
                        <SelectTrigger
                          className={`w-full border ${statusMeta.pill}`}
                          data-testid={`aep-status-${row.carrier_id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} data-testid={`aep-status-opt-${opt.value}`}>
                              <span className="inline-flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${opt.dot}`} />
                                {opt.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {row.updated_at && (
                        <p className="text-[10px] text-slate-400 mt-1 text-right">
                          Updated {new Date(row.updated_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {readOnly && (
        <div className="text-xs text-slate-500 flex items-center gap-1 pl-1">
          <AlertCircle size={12} /> Read-only view — only the agent can update their own certifications.
        </div>
      )}
    </div>
  );
};

// Phase 27.30.18 — Reusable logo tile for the internal AEP tab.
function InternalCarrierLogo({ row }) {
  const [failed, setFailed] = React.useState(false);
  const url = row.logo_url && !failed
    ? (row.logo_url.startsWith('http')
        ? row.logo_url
        : `${process.env.REACT_APP_BACKEND_URL}${row.logo_url}`)
    : null;
  const initials = (row.carrier_name || '')
    .split(/[\s/-]+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]).join('').toUpperCase() || '?';
  return (
    <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0 p-2 md:p-2.5">
      {url
        ? <img src={url} alt={row.carrier_name} className="w-full h-full object-contain" onError={() => setFailed(true)} />
        : <span className="text-slate-500 font-semibold text-lg">{initials}</span>}
    </div>
  );
}

export default AepCertificationsTab;
